import React from 'react';
import { CheckCircle, CreditCard } from 'lucide-react';
import PatientCard from './PatientCard';
import { formatDateTimeIST } from '../utils/date';

function toProperCase(str) {
  if (!str) return '';
  return str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

const STATUS_PILL = {
  'Triage Pending': 'bg-purple-50 text-purple-700 border-purple-200',
  'Awaiting Consult': 'bg-blue-50 text-blue-700 border-blue-200',
  'In Consult': 'bg-amber-50 text-amber-800 border-amber-200',
  Completed: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  Cancelled: 'bg-red-50 text-red-700 border-red-200',
};

/** Doctor dashboard appointment card — same premium layout as queue PatientCard. */
export default function DoctorAppointmentCard({
  appt, index = 0, onOpen, onFamily, onVitals,
}) {
  const displayStatus = appt.display_status || appt.status || '';
  const statusCls = STATUS_PILL[displayStatus] || 'bg-zinc-50 text-zinc-700 border-zinc-200';
  const name = `${appt.salutation ? `${appt.salutation} ` : ''}${toProperCase(appt.patient_name)}`.trim();

  return (
    <PatientCard
      variant="doctor"
      patient={{
        id: appt.patient_id || appt.appointment_id,
        name: name || 'Patient',
        phone: appt.phone,
        age: appt.age,
        gender: appt.gender,
        last_visit_at: appt.last_visit_at || appt.last_consult_at,
      }}
      onOpen={() => onOpen?.(appt)}
      onFamily={onFamily ? () => onFamily(appt) : undefined}
      onVitals={onVitals ? () => onVitals(appt) : undefined}
      badge={(
        <div className="flex items-center gap-1 flex-wrap" data-testid={`appt-card-${index}`}>
          <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ${statusCls}`}>
            {displayStatus || 'Scheduled'}
          </span>
          {appt.visit_type ? (
            <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-sky-50 text-sky-800 border border-sky-200">
              {appt.visit_type}
            </span>
          ) : null}
          {appt.appointment_type ? (
            <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-800 border border-indigo-200">
              {appt.appointment_type}
            </span>
          ) : null}
          {appt.time ? (
            <span className="text-[10px] font-semibold text-zinc-500">
              {formatDateTimeIST(appt.date, appt.time)}
            </span>
          ) : null}
          {appt.consultation_status === 'completed' && appt.pharmacy_posted ? (
            <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200">
              <CheckCircle className="h-3 w-3" /> Rx
            </span>
          ) : null}
          <span
            className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded border ${
              appt.payment_done
                ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                : 'bg-orange-50 text-orange-800 border-orange-200'
            }`}
          >
            <CreditCard className="h-3 w-3" />
            {appt.payment_done ? 'Paid' : 'Unpaid'}
          </span>
        </div>
      )}
    />
  );
}
